import { GridApi } from 'ag-grid-community'
import { Negotiation, NegotiationPackageDetail, EntityType } from 'api'
import { noop } from 'lodash'
import React, { useContext, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { GlobalState } from 'store'
import { RepositoryActions } from 'store/repository'
import { NEGOTIATIONS_PATH } from 'view/components/datagrid/renderers/negotiationDataUtil'
import {
  curveNegotationPackageItemSelectedSelector,
  getNegotiationPackagesSelectedSelector,
  CURVE_NEGOTIATION_SELECTED_META_NAME
} from './CurveNegotiationGridSelectors'

interface CurveGridSelectionContext {
  onGridSelectionChanged: (onChangeselectionIds: string[]) => void
  onNegotiationSelected: (negotiationId: string, isSelected: boolean) => void
  isNegotiationSelected: (negotiaiton: Negotiation) => boolean
}
const CurveGridSelectionContext = React.createContext<CurveGridSelectionContext>({
  onGridSelectionChanged: noop,
  onNegotiationSelected: noop,
  isNegotiationSelected: () => false
})
export const useCurveGridSelectionContext = () => useContext(CurveGridSelectionContext)
type SelectionChangeHandler = (rowNodeId: string, selected: boolean) => void

export const CurveGridSelectionProvider: React.FC<{
  children?: React.ReactNode
  gridApi: GridApi | undefined
  negotiationPackages: NegotiationPackageDetail[]
}> = ({ gridApi, negotiationPackages, children }) => {
  const dispatch = useDispatch()
  const negotiationPackageItemSelected = useSelector(curveNegotationPackageItemSelectedSelector)

  const getSelected = useSelector((state: GlobalState) => (n: NegotiationPackageDetail) => getNegotiationPackagesSelectedSelector(n)(state))
  const selectionByPackageItems = useMemo(
    () => negotiationPackages.reduce<string[]>((selections, pkg) => (getSelected(pkg) ? [pkg.uuid, ...selections] : selections), []),
    [getSelected, negotiationPackages]
  )

  // items + package selection => grid selection
  useEffect(() => {
    const currentGridSelection = (gridApi?.getSelectedNodes() ?? []).map(n => n.data.uuid)
    const toRemove = currentGridSelection.filter(id => !selectionByPackageItems.includes(id))
    const toAdd = selectionByPackageItems.filter(id => !currentGridSelection.includes(id))
    const all = [...toAdd, ...toRemove]
    gridApi?.forEachNode(node => {
      const uuid = node?.data?.uuid
      if (uuid && all.includes(uuid)) {
        node.setSelected(toRemove.includes(uuid) ? false : true)
      }
    })
  }, [selectionByPackageItems, gridApi])

  // grid selected => update package items
  const handleSelectionChanged: SelectionChangeHandler = (rowNodeId: string, selected: boolean) => {
    const pkg = negotiationPackages.find(({ uuid }) => uuid === rowNodeId)
    if (pkg) {
      Object.values(pkg[NEGOTIATIONS_PATH]).map(({ negotiation }) =>
        dispatch(
          RepositoryActions.updateMeta(EntityType.NEGOTIATION, negotiation.negotiationId, CURVE_NEGOTIATION_SELECTED_META_NAME, selected, {
            ownOrder: false,
            persist: true
          })
        )
      )
    }
  }

  const onGridSelectionChanged = (selectionIds: string[]) => {
    selectionIds.filter(id => !selectionByPackageItems.includes(id)).map(ids => handleSelectionChanged(ids, true))
    selectionByPackageItems.filter(id => !selectionIds.includes(id)).map(ids => handleSelectionChanged(ids, false))
  }

  // item selector
  const onNegotiationSelected = (negotiationId: string, isSelected: boolean) =>
    dispatch(
      RepositoryActions.updateMeta(EntityType.NEGOTIATION, negotiationId, CURVE_NEGOTIATION_SELECTED_META_NAME, isSelected, {
        ownOrder: false,
        persist: true
      })
    )

  return (
    <CurveGridSelectionContext.Provider
      value={{ onGridSelectionChanged, onNegotiationSelected, isNegotiationSelected: negotiationPackageItemSelected }}>
      {children}
    </CurveGridSelectionContext.Provider>
  )
}
